import React from "react";
import ReactDOM from "react-dom";

import { FlagGroup, IFlagGroupColor } from "@multiweb/reactComponents";
import { TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils/constants";
import { FlagColor } from "@multiweb/types/react";
import { ITextBuilder } from "Global/Base/Builders/ITextBuilder";
import { Key } from "Global/Base/Utils/EventUtils";
import { FlagUtils } from "Global/Base/Utils/FlagUtils";
import { SetUtils } from "Global/Base/Utils/SetUtils";
import { Enum } from "OW.Remoting/Remoting/DataTypes/Enums";
import { Flags } from "OW.Remoting/Remoting/DataTypes/Flags";
import { createElement, ICreateElement } from "UI/UI.ElementarControls/Utils/CustomElementUtils";
import { PopupMenu2, PopupMenuOptions } from "../PopupMenu2";
import { ControlParams } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export type FlagEditParams = ControlParams & {
    attrName?: string;
};

export class FlagEdit extends EditableControl {
    private _permanentVisible: boolean = false;
    private _multiselect: boolean = false;
    private _inputElement: HTMLElement;
    private _flagCirclePopup: PopupMenu2;
    private _flagList: Array<string> = [];
    private _flagColorsNameMap: Map<string, string> = new Map;

    private readonly _hideValues: string[] = [];

    private _controlValues: Set<string> = new Set;

    public override get controlValue(): Enum {
        if (this._controlValues.size > 0) return Flags.get(SetUtils.first(this._controlValues));

        return null;
    }

    public override set controlValue(value: Enum) {
        this._controlValues.clear();

        if (value != null) this._controlValues.add(value.getName());

        this._updateSelectedValues();
    }

    public override readonly attrName: string;

    /**
     * Zobrazena hodnota v komponente
     */
    public override get displayedValue(): ITextBuilder {
        return SetUtils.first(this._controlValues);
    }

    public override set displayedValue(v: ITextBuilder) {
        this._controlValues.add(v.toString());
        if (this.validate()) {
            this._updateSelectedValues();
        }
    }

    constructor({ attrName, ...otherParams }: FlagEditParams) {
        super(otherParams);

        this.element.classList.add("flag-edit");
        this._createElements();
        this.attrName = attrName;

        if (this.element.getAttribute("data-hideValues"))
            this._hideValues = this.getDataAttr("hideValues").split(/\s*;\s*/);

        Flags.loadingPromise.then(loadedFlags => {
            if (loadedFlags) {
                this._flagList = Object.keys(loadedFlags);
                this.initializeTitles();
            }
        });
    }

    public parse(value: string): void {
        this.controlValue = Flags.get(value);
    }

    /** Vraci true - krome Editu pouzitelne pro vsechny inputy */
    public override validate(): boolean {
        this.setValid();
        for (const color of this._controlValues) {
            if (!this._flagList.includes(color) || this._hideValues.includes(color)) return false;
        }
        return true;
    }

    private _onColorSelectedChange(color: string, state: boolean): void {
        if (!this._multiselect) this._controlValues.clear();

        const colorName = this._flagList.find(f => f.toLowerCase().includes(color));

        if (state) this._controlValues.add(colorName);
        else this._controlValues.delete(colorName);

        this._updateSelectedValues();
        if (this._flagCirclePopup) this._flagCirclePopup.close();
        this.valueChanged.trigger(this, { Sender: this });
    }

    private _updateSelectedValues(): void {
        this._inputElement.innerHTML = "";
        for (const flagColor of this._controlValues) {
            const color = FlagUtils.flagToCss(flagColor);
            this._inputElement.appendChild(createElement({
                attrs: {
                    "data-color": color,
                },
            }));
        }
    }

    public initializeTitles(): void { // [JB] TODO: Předělat na i18n jakmile bude dostupné
        this._flagList.forEach(
            async (color) =>
                this._flagColorsNameMap.set(color, await Flags.get(color).getLocalizedName()),
        );
    }

    private _createElements(): void {
        if (this._permanentVisible) this.element.classList.add("flag-edit--permanent-visible");
        this._inputElement = createElement({ classes: "flag-edit__input-element" });
        this._inputElement.tabIndex = TAB_INDEX_FOCUSABLE_TAB_SEQUENCE;

        this._inputElement.addEventListener("focus", () => {
            this.element.classList.add("focused");
        });
        this._inputElement.addEventListener("blur", () => {
            this.element.classList.remove("focused");
        });

        const params = { type: "button", classes: "valuepicker" } as ICreateElement;
        if (this._captionLabel != null) { // pokud je komponenta na DF, chceme provázat label s input elementem
            params.id = this._captionLabel.getAttribute("for");
        }
        const pickerButton = createElement(params);

        this.element.addEventListener("click", (e) => {
            this._showPopup(e);
        });

        this.element.addEventListener("keydown", (e) => {
            if (e.key === Key.Enter || e.key === Key.Space) {
                this._showPopup(e);
                e.preventDefault();
            }
        });

        this.element.appendChild(this._inputElement);
        this.element.appendChild(pickerButton);
    }

    private _showPopup(e: MouseEvent | KeyboardEvent): void {
        if (!this.enabledVisual || e.ctrlKey || e.metaKey) {
            return;
        }
        if (this._flagCirclePopup && this._flagCirclePopup.visible) {
            this._flagCirclePopup.dispose();
            this._flagCirclePopup = null;
            return;
        }

        const popup = createElement({ classes: "flag-edit__popup" });
        const selectedValue = SetUtils.first(this._controlValues);
        const flags: Array<IFlagGroupColor> = new Array<IFlagGroupColor>();

        for (const color of this._flagList) {
            const isSelected = (selectedValue == null && color === "fNone") ? true : this._controlValues.has(color);

            if (this._hideValues.includes(color) && !isSelected) {
                continue;
            }

            flags.push({ color: (color[0] === "f" ? color.substring(1) : color).toLowerCase() as FlagColor, isSelected: isSelected, title: this._flagColorsNameMap.get(color) });
        }

        ReactDOM.render(React.createElement(FlagGroup, { flags, focusFirstItem: e instanceof KeyboardEvent, onChange: (color, isSelected) => this._onColorSelectedChange(color, isSelected) }), popup);

        if (this._flagCirclePopup) {
            this._flagCirclePopup.dispose();
            this._flagCirclePopup = null;
        }
        this._flagCirclePopup = new PopupMenu2({
            target: this.element,
            content: popup,
        } as PopupMenuOptions);
        this._flagCirclePopup.show(e);
    }

    /**
     * Zahájí editaci otevřením nabídky FlagEditu.
     *
     * @see IStartEdit
     */
    public override startEdit(): void {
        /**
         * Událost z fake komponenty, která spouští startEdit, musí být zpracovaná před tím, než je vytvořený popup na základě volání
         * this.element.click(). V opačném případě se událost dostane k window, kde ji zachytí _outsideClickListener z Popupu, což má
         * za následek, že se Popup po otevření ihned zase zavře.
         */
        setTimeout(() => {
            this.element.click();
        }, 0);
    }
}
